// @flow

import stringifyObject from 'stringify-object';
import { get } from 'lodash';

import { getDevice } from './PrePayment.helpers';

export function getPrePaymentDetailsQuery(bookingId: string) {
  return `query {
    userBooking(bookingId:"${bookingId}") {
      paymentDetails {
        duePayment {
          deadline
          amount {
            value
          }
        }
      }
      information {
        status
        bookingId
        stays {
          id
          departureDate
          returnDate
          departureCity
          arrivalCity
          adults
          children
          resort {
            name
            image
            comfort
            type
            icon
            badgeIcon
            isJoyview
            destination {
              country
            }
            departureCity
            arrivalCity
          }
        }
        vendor {
          isSoldByClubMed
          name
          phoneNumber
        }
        isPaid
      }
    }

    legalInformations {
      optionConfirmation {
        id
        label
        description
        url
        required
        preChecked
      }
    }

    paymentProviders {
      id
      label
      logo
      description
      requiredDelayBeforeDeparture
      paymentMethods {
        id
        label
        image
        category
        timePaymentConditions {
          id
          paymentCount
          chargePercentage
        }
      }
      token {
        format
        value
      }
    }
  }`;
}

type Package = {
  id: string,
  attendee_ids: Array<string>,
};

export type InitPayload = {
  bookingId?: string,
  amount?: string,
  currency?: string,
  service?: {
    packages?: Array<Package>,
    roomSelectionServicePayload?: *,
    count?: number,
  },
  action?: string,
  urlRedirectionIfNothingToPay?: string,
  beforeRedirect?: () => void,
};

export function getInitPaymentQuery(payload: InitPayload) {
  const { bookingId = '', service, action = '' } = payload;

  const roomSelectionServicePayload = stringifyObject(
    get(service, 'roomSelectionServicePayload', null),
    { singleQuotes: false },
  );
  const stringifiedService = stringifyObject(
    {
      amount: get(service, 'amount', null),
      currency: get(service, 'currency', null),
    },
    { singleQuotes: false },
  );

  return `query {
    prePayment(bookingId: "${bookingId}", service: ${stringifiedService}, roomSelectionServicePayload:${roomSelectionServicePayload}, action:"${action}") {
      due {
        value
        formattedValue(precision: 2)
        currency
      }
      paymentSchedule {
        deposit {
          value
          formattedValue(precision: 2)
        }
        remainingBalance {
          value
          formattedValue(precision: 2)
        }
        dueDate
      }
      bookingBalance(precision: 2)
      serviceFormattedPrice(precision: 2)
      remainingCreditBalance(precision: 2)
    }
  }`;
}

type Payload = {
  bookingId: string,
  providerId: string,
  redirectUri: string,
  redirectCancelUrl: string,
  amount: string,
  currency: string,
  responsive: ResponsiveProps,
  service: {
    packages: Array<Package>,
  },
  paymentConditionId?: string,
  action?: string,
  token?: string,
  isIframe: boolean,
  iframeCallbackUrl: string,
};

export function getFormDataQuery({
  bookingId,
  providerId,
  redirectUri,
  amount,
  currency,
  service,
  redirectCancelUrl,
  responsive,
  paymentConditionId,
  action,
  token: tokenParam,
  isIframe,
  iframeCallbackUrl,
}: Payload) {
  const paymentConditionQuery = paymentConditionId
    ? `paymentConditionId : "${paymentConditionId}"`
    : '';
  const act = action ? `action: "${action}"` : '';
  const token = tokenParam ? `token: "${tokenParam}"` : '';
  return `mutation {
    bookingsPaymentRedirect: bookingPaymentRedirect (
      bookingId: "${bookingId}",
      providerId:"${providerId}",
      redirectUri: "${redirectUri}",
      redirectCancelUrl:"${redirectCancelUrl}",
      amount: ${amount},
      currency:"${currency}",
      packages: ${stringifyObject(get(service, 'packages', []), { singleQuotes: false })},
      additionalService:${stringifyObject(get(service, 'additionalService', null), {
        singleQuotes: false,
      })},
      roomSelectionServices:${stringifyObject(get(service, 'roomSelectionServicePayload', null), {
        singleQuotes: false,
      })},
      device: "${getDevice(responsive)}"
      ${paymentConditionQuery}
      ${act}
      ${token}
      isIframePayment: ${(isIframe || false).toString()}
      iframeCallbackUrl: "${iframeCallbackUrl}"
    ) {
      url
      fields
      method
      bookingId
      customerId
      price {
        value
      }
    }
  }`;
}
